import { Component, OnInit, OnDestroy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { OverlayContainer } from "@angular/cdk/overlay";
import { DogService } from "src/app/core/services/dog.service";
import { DogSummary } from "src/app/shared/models/dog-summary";
import { CustomerService } from "src/app/core/services/customer.service";
import { NotificationMessageComponent } from "src/app/shared/components/notification/notification-message/notification-message.component";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatButtonModule } from "@angular/material/button";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { Observable, forkJoin, Subject } from "rxjs";
import { takeUntil, finalize, catchError } from "rxjs/operators";
import { of } from "rxjs";

interface DogFormData {
  files: File[];
  dateSelected?: Date;
}

@Component({
  selector: "app-upload-vaccination",
  templateUrl: "./upload-vaccination.component.html",
  styleUrls: ["./upload-vaccination.component.scss"],
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatProgressSpinnerModule]
})
export class UploadVaccinationComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private container: OverlayContainer;

  // Loading data
  uploadLoading: boolean = false;
  uploadError: string | null = null;

  // State data
  disableUploadButton: boolean = true;
  dogData: DogSummary[] = [];
  dogFormData: Map<number, DogFormData> = new Map<number, DogFormData>();
  vaxMinDate = new Date();
  startDate = new Date(this.vaxMinDate.getFullYear() + 1, this.vaxMinDate.getMonth(), this.vaxMinDate.getDate());

  constructor(
    overlayContainer: OverlayContainer,
    private dialog: MatDialog,
    private dogService: DogService,
    private customerService: CustomerService
  ) {
    // Creates the blue backdrop for the dialog.
    overlayContainer.getContainerElement().classList.add("broadcast-dialog_overlay");
    this.container = overlayContainer;
  }

  ngOnInit(): void {
    this.fetchDogInfo();
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to unsubscribe from all observables
    this.destroy$.next();
    this.destroy$.complete();

    // Removes the blue backdrop for the dialog.
    this.container.getContainerElement().classList.remove("broadcast-dialog_overlay");
  }

  onFileSelected(event: Event, dogId: number): void {
    const target = event.target as HTMLInputElement;
    if (!target.files) {
      return;
    }

    let currentData: DogFormData | undefined = this.dogFormData.get(dogId);
    if (!currentData) {
      currentData = { files: [] };
    }

    for (let i = 0; i < target.files.length; i++) {
      const file = target.files[i];
      if (file) {
        currentData.files.push(file);
      }
    }

    this.dogFormData.set(dogId, currentData);
    this.checkUploadButtonState();
  }

  onRemoveFile(event: Event, file: File, dogId: number): void {
    event.preventDefault();

    const currentData: DogFormData | undefined = this.dogFormData.get(dogId);
    if (!currentData) {
      return;
    }

    currentData.files = currentData.files.filter((f: File) => f.name !== file.name);
    this.dogFormData.set(dogId, currentData);
    this.checkUploadButtonState();
  }

  hasFiles(dogId: number): boolean {
    const data = this.dogFormData.get(dogId);
    return data ? data.files.length > 0 : false;
  }

  checkUploadButtonState(): void {
    this.disableUploadButton = true;
    this.dogFormData.forEach((formData: DogFormData) => {
      if (formData.files.length > 0) {
        this.disableUploadButton = false;
      }
    });
  }

  onSubmit(event: Event): void {
    event.preventDefault();

    if (this.uploadLoading || this.disableUploadButton) {
      return;
    }

    this.uploadLoading = true;
    this.uploadError = null;

    const subscriptions: Observable<any>[] = [];

    this.dogFormData.forEach((data: DogFormData, dogId: number) => {
      if (data.files.length === 0) {
        return; // Skip dogs with no files
      }

      const vaxRecordsFormData = new FormData();
      vaxRecordsFormData.append("dog_id", dogId.toString());

      if (data.dateSelected) {
        vaxRecordsFormData.append("expiry_date", data.dateSelected.toString());
      }

      for (const file of data.files) {
        vaxRecordsFormData.append("files[]", file, file.name);
      }

      subscriptions.push(
        this.customerService.uploadVaccinationRecords(vaxRecordsFormData).pipe(
          catchError(error => {
            console.error("Upload error for dog:", dogId, error);
            return of({ success: false, dogId, error });
          })
        )
      );
    });

    if (subscriptions.length === 0) {
      this.uploadLoading = false;
      return;
    }

    // Wait for all subscriptions to finish before continuing
    forkJoin(subscriptions)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.uploadLoading = false;
        })
      )
      .subscribe({
        next: results => {
          const hasErrors = results.some((result: any) => result.success === false);
          if (hasErrors) {
            this.uploadError = "Some uploads failed. Please try again.";
          } else {
            this.showSuccessDialog();
          }
        },
        error: error => {
          console.error("Upload error:", error);
          this.uploadError = "Failed to upload vaccination records. Please try again.";
        }
      });
  }

  showSuccessDialog(): void {
    const successDialog = this.dialog.open(NotificationMessageComponent, {
      data: {
        heading: "Records uploaded!",
        body: "Your records were updated successfully.",
        type: "success"
      }
    });

    successDialog
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        window.location.reload();
      });
  }

  fetchDogInfo(): void {
    this.dogService
      .getDogsSummary()
      .pipe(
        takeUntil(this.destroy$),
        catchError(error => {
          console.error("Failed to fetch dog info:", error);
          this.uploadError = "Failed to load dog information. Please refresh the page.";
          return of({ api_dogs: [] });
        })
      )
      .subscribe((res: any) => {
        this.dogData = res.api_dogs || [];
      });
  }
}
